import { Button, ButtonGroup, Select} from '@tunasong/ui-lib'
import type { KeyNameType } from '@tunasong/music-lib'
import { getKeyNames, prettifyNote, scaleNoteNames } from '@tunasong/music-lib'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import { Typography, useDimensions } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './scale-select.module.css'

export interface ScaleSelectProps {
  className?: string
  value?: Partial<Scale> | null
  title?: string
  fullWidth?: boolean
  variant?: KeyNameType
  allowNone?: boolean
  onChange(scale: Scale | null): void
}

export const ScaleSelect: FC<ScaleSelectProps> = props => {
  const { value = null, allowNone = true, onChange, variant = 'common' } = props

  const { isSmallOrSmaller } = useDimensions()

  const handleScaleType = (type: string | null) => {
    const scale = type ? { root: value?.root ?? 'C', type: type as ScaleType } : null
    onChange(scale)
  }

  const handleRoot = (note?: NoteName) => {
    onChange(note ? { root: note, type: value?.type ?? 'major' } : null)
  }

  const keyNames = getKeyNames(variant)

  const showModes = variant === 'all' || variant === 'mode'

  return (
    <>
      <div
        style={{
          marginTop: 8,
          gap: 16,
          alignItems: isSmallOrSmaller ? 'flex-start' : 'center',
          display: 'flex',
          flexDirection: isSmallOrSmaller ? 'column' : 'row',
        }}
      >
        <Typography variant="subtitle2" className={styles.rootLabel}>
          {props.title ?? 'Root'}
        </Typography>
        <ButtonGroup variant="outlined" size="small" className={styles.buttonGroupWrap}>
          {allowNone ? (
            <Button
              className={styles.noneButton}
              variant={value === null ? 'contained' : 'outlined'}
              color={value === null ? 'primary' : 'inherit'}
              onClick={() => handleScaleType(null)}
            >
              <i>None</i>
            </Button>
          ) : null}

          {scaleNoteNames.map(note => (
            <Button
              className={styles.noteButton}
              key={note}
              variant={value?.root === note ? 'contained' : 'outlined'}
              color={value?.root === note ? 'primary' : 'inherit'}
              onClick={() => handleRoot(note)}
            >
              {prettifyNote(note)}
            </Button>
          ))}
        </ButtonGroup>

        <div style={{ minWidth: 200, position: 'relative', marginBottom: showModes ? 16 : 0 }}>
          <Select
            label="Scale Type"
            value={value?.type ?? ''}
            onChange={e => handleScaleType(e.target.value || null)}
            disabled={value === null}
          >
            <option value="">Select scale type...</option>
            {keyNames.map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </>
  )
}
